import { useCallback, useEffect, useState } from "react";
import { api, formatError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { Plus, Wrench } from "lucide-react";

export default function MaintenancePage() {
  const [tasks, setTasks] = useState([]);
  const [plots, setPlots] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ plot_id: "", title: "", description: "", priority: "medium", assigned_to: "" });

  const load = useCallback(async () => {
    const [t, p, u] = await Promise.all([api.get("/maintenance"), api.get("/plots"), api.get("/users?role=field_worker")]);
    setTasks(t.data); setPlots(p.data); setWorkers(u.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      await api.post("/maintenance", payload);
      toast.success("Task created");
      setOpen(false); setForm({ plot_id: "", title: "", description: "", priority: "medium", assigned_to: "" });
      load();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const filtered = filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  const plotOf = (id) => plots.find((p) => p.id === id);
  const workerOf = (id) => workers.find((w) => w.id === id);

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Wrench className="text-primary" />
          <h1 className="heading text-3xl md:text-4xl">Maintenance</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button data-testid="new-task-btn"><Plus size={14} className="mr-2" />New task</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New maintenance task</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Plot</Label>
                <Select value={form.plot_id} onValueChange={(v) => setForm({ ...form, plot_id: v })}>
                  <SelectTrigger data-testid="task-plot-select"><SelectValue placeholder="Select plot" /></SelectTrigger>
                  <SelectContent>{plots.map((p) => (<SelectItem key={p.id} value={p.id}>Plot {p.plot_number}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div><Label>Title</Label><Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="task-title-input" /></div>
              <div><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
              <div>
                <Label>Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["low", "medium", "high"].map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Assign to</Label>
                <Select value={form.assigned_to} onValueChange={(v) => setForm({ ...form, assigned_to: v })}>
                  <SelectTrigger data-testid="task-assign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent>{workers.map((w) => (<SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <DialogFooter><Button type="submit" disabled={!form.plot_id || !form.title} data-testid="task-save-btn">Create</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {["all", "pending", "in_progress", "blocked", "completed"].map((s) => (
          <button key={s} onClick={() => setFilter(s)} className={`text-xs uppercase tracking-widest px-3 py-1.5 rounded-md border ${filter === s ? "bg-primary text-primary-foreground border-primary" : "border-border hover:bg-secondary"}`} data-testid={`filter-${s}`}>
            {s.replace("_", " ")}
          </button>
        ))}
      </div>

      <div className="border border-border rounded-lg bg-card divide-y divide-border">
        {filtered.length === 0 && <div className="p-6 text-sm text-muted-foreground">No tasks.</div>}
        {filtered.map((t) => (
          <div key={t.id} className="p-4 flex flex-wrap items-start justify-between gap-3" data-testid={`admin-task-${t.id}`}>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium">{t.title}</div>
              {t.description && <div className="text-xs text-muted-foreground mt-1">{t.description}</div>}
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2">
                Plot {plotOf(t.plot_id)?.plot_number || "—"} · Priority {t.priority} · Assigned {workerOf(t.assigned_to)?.name || "—"}
              </div>
              {t.history?.length > 0 && (
                <details className="mt-2">
                  <summary className="text-[10px] uppercase tracking-widest text-muted-foreground cursor-pointer">History ({t.history.length})</summary>
                  <ul className="mt-2 space-y-1 text-xs text-muted-foreground">
                    {t.history.map((h) => (
                      <li key={`${h.at}-${h.status}`}>· {h.status.replace("_", " ")} · {new Date(h.at).toLocaleString()} · {h.by}{h.reason ? ` · "${h.reason}"` : ""}</li>
                    ))}
                  </ul>
                </details>
              )}
            </div>
            <span className={`status-pill status-${t.status}`}>{t.status.replace("_", " ")}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
